import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import TextField from 'components/TextField';
import Section from '../../components/Section';
import { InputAdornment } from '@material-ui/core';
import NaturezasActions from 'store/ducks/naturezas';
import { Container, Row, Column, Error, WrapperInputCustom, AutocompleteWrapper, AutocompleteDropdown, AutocompleteItem } from './styles';

function Totais({ formik, edit, view }) {
  const dispatch = useDispatch();
  const { naturezas: naturezasState } = useSelector((state) => state.naturezas);
  const [naturezaAdiantamentoInput, setNaturezaAdiantamentoInput] = useState('');
  const [showDropdownNaturezaAdiantamento, setShowDropdownNaturezaAdiantamento] = useState(false);
  const dropdownNaturezaAdiantamentoRef = useRef(null);
  const [naturezaFreteInput, setNaturezaFreteInput] = useState('');
  const [showDropdownNaturezaFrete, setShowDropdownNaturezaFrete] = useState(false);
  const dropdownNaturezaFreteRef = useRef(null);

  // Converter naturezas de Immutable para array normal
  const naturezas = useMemo(() => {
    if (!naturezasState) return [];
    
    if (Array.isArray(naturezasState)) {
      return naturezasState;
    }
    
    if (naturezasState && typeof naturezasState === 'object') {
      if (typeof naturezasState.asMutable === 'function') {
        try {
          const mutable = naturezasState.asMutable({ deep: true });
          return Array.isArray(mutable) ? mutable : [];
        } catch (e) {
          console.warn('Erro ao converter Immutable:', e);
        }
      }
      
      if (typeof naturezasState.length === 'number') {
        try {
          return Array.from(naturezasState);
        } catch (e) {
          console.warn('Erro ao converter com Array.from:', e);
        }
      }
    }
    
    return [];
  }, [naturezasState]);

  // Carregar naturezas
  useEffect(() => {
    dispatch(NaturezasActions.getNaturezasRequest());
  }, [dispatch]);

  // Sincronizar inputs quando formik mudar
  useEffect(() => {
    if (formik.values.naturezaAdiantamentoId && naturezas.length > 0) {
      const natureza = naturezas.find(n => String(n.id) === String(formik.values.naturezaAdiantamentoId));
      if (natureza) {
        setNaturezaAdiantamentoInput(`${natureza.codigo || ''} - ${natureza.descricao || ''}`);
      }
    }
  }, [formik.values.naturezaAdiantamentoId, naturezas]);

  useEffect(() => {
    if (formik.values.naturezaFreteId && naturezas.length > 0) {
      const natureza = naturezas.find(n => String(n.id) === String(formik.values.naturezaFreteId));
      if (natureza) {
        setNaturezaFreteInput(`${natureza.codigo || ''} - ${natureza.descricao || ''}`);
      }
    }
  }, [formik.values.naturezaFreteId, naturezas]);

  // Filtrar naturezas
  const naturezasAdiantamentoFiltradas = useMemo(() => {
    if (!naturezaAdiantamentoInput || naturezaAdiantamentoInput.trim() === '') {
      return naturezas;
    }
    const searchTerm = naturezaAdiantamentoInput.toLowerCase();
    return naturezas.filter((n) => {
      const codigo = String(n.codigo || '').toLowerCase();
      const descricao = String(n.descricao || '').toLowerCase();
      return codigo.includes(searchTerm) || descricao.includes(searchTerm);
    });
  }, [naturezaAdiantamentoInput, naturezas]);

  const naturezasFreteFiltradas = useMemo(() => {
    if (!naturezaFreteInput || naturezaFreteInput.trim() === '') {
      return naturezas;
    }
    const searchTerm = naturezaFreteInput.toLowerCase();
    return naturezas.filter((n) => {
      const codigo = String(n.codigo || '').toLowerCase();
      const descricao = String(n.descricao || '').toLowerCase();
      return codigo.includes(searchTerm) || descricao.includes(searchTerm);
    });
  }, [naturezaFreteInput, naturezas]);

  // Selecionar naturezas
  const handleSelecionarNaturezaAdiantamento = (natureza) => {
    formik.setFieldValue('naturezaAdiantamentoId', natureza.id);
    setNaturezaAdiantamentoInput(`${natureza.codigo || ''} - ${natureza.descricao || ''}`);
    setShowDropdownNaturezaAdiantamento(false);
  };

  const handleSelecionarNaturezaFrete = (natureza) => {
    formik.setFieldValue('naturezaFreteId', natureza.id);
    setNaturezaFreteInput(`${natureza.codigo || ''} - ${natureza.descricao || ''}`);
    setShowDropdownNaturezaFrete(false);
  };

  // Fechar dropdowns ao clicar fora
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownNaturezaAdiantamentoRef.current && !dropdownNaturezaAdiantamentoRef.current.contains(event.target)) {
        setShowDropdownNaturezaAdiantamento(false);
      }
      if (dropdownNaturezaFreteRef.current && !dropdownNaturezaFreteRef.current.contains(event.target)) {
        setShowDropdownNaturezaFrete(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);
  return (
    <Section title="Totais">
      <Container>
        <Row>
          <Column>
            <WrapperInputCustom>
              <TextField
                label="Quantidade CT-e"
                name="quantidade_cte"
                type="number"
                placeholder="0"
                value={formik.values.quantidade_cte ?? ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.quantidade_cte && Boolean(formik.errors.quantidade_cte)}
                helperText={formik.touched.quantidade_cte && formik.errors.quantidade_cte}
                disabled={view}
                fullWidth
                color="secondary"
              />
            </WrapperInputCustom>
          </Column>

          <Column>
            <WrapperInputCustom>
              <TextField
                label="Quantidade NFe"
                name="quantidade_nfe"
                type="number"
                placeholder="0"
                value={formik.values.quantidade_nfe ?? ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.quantidade_nfe && Boolean(formik.errors.quantidade_nfe)}
                helperText={formik.touched.quantidade_nfe && formik.errors.quantidade_nfe}
                disabled={view}
                fullWidth
                color="secondary"
              />
            </WrapperInputCustom>
          </Column>

          <Column>
            <WrapperInputCustom>
              <TextField
                label="Quantidade MDF-e"
                name="quantidade_mdfe"
                type="number"
                placeholder="0"
                value={formik.values.quantidade_mdfe ?? ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.quantidade_mdfe && Boolean(formik.errors.quantidade_mdfe)}
                helperText={formik.touched.quantidade_mdfe && formik.errors.quantidade_mdfe}
                disabled={view}
                fullWidth
                color="secondary"
              />
            </WrapperInputCustom>
          </Column>
        </Row>

        <Row>
          <Column>
            <WrapperInputCustom>
              <TextField
                label="Valor da Carga"
                name="valor_carga"
                type="number"
                inputProps={{ step: "0.01" }}
                placeholder="0.00"
                value={formik.values.valor_carga ?? ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.valor_carga && Boolean(formik.errors.valor_carga)}
                helperText={formik.touched.valor_carga && formik.errors.valor_carga}
                disabled={view}
                fullWidth
                color="secondary"
              />
            </WrapperInputCustom>
          </Column>

          <Column>
            <WrapperInputCustom>
              <TextField
                label="Peso Bruto"
                name="peso_bruto"
                type="number"
                inputProps={{ step: "0.01" }}
                placeholder="0.00"
                value={formik.values.peso_bruto ?? ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.peso_bruto && Boolean(formik.errors.peso_bruto)}
                helperText={formik.touched.peso_bruto && formik.errors.peso_bruto}
                disabled={view}
                fullWidth
                color="secondary"
              />
            </WrapperInputCustom>
          </Column>

          <Column>
            <WrapperInputCustom>
              <TextField
                label="Peso Líquido"
                name="peso_liquido"
                type="number"
                inputProps={{ step: "0.01" }}
                placeholder="0.00"
                value={formik.values.peso_liquido ?? ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.peso_liquido && Boolean(formik.errors.peso_liquido)}
                helperText={formik.touched.peso_liquido && formik.errors.peso_liquido}
                disabled={view}
                fullWidth
                color="secondary"
              />
            </WrapperInputCustom>
          </Column>
        </Row>

        <Row>
          <Column>
            <WrapperInputCustom>
              <TextField
                label="Frete Embarcador"
                name="freight_value"
                type="number"
                inputProps={{ step: "0.01", min: "0" }}
                placeholder="0,00"
                value={formik.values.freight_value ?? ''}
                onChange={(e) => {
                  formik.handleChange(e);
                  formik.setFieldError('freight_value', '');
                }}
                onBlur={formik.handleBlur}
                error={formik.touched.freight_value && Boolean(formik.errors.freight_value)}
                helperText={formik.touched.freight_value && formik.errors.freight_value}
                disabled={view}
                fullWidth
                color="secondary"
                InputProps={{
                  startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                }}
              />
            </WrapperInputCustom>
          </Column>

          <Column>
            <WrapperInputCustom>
              <TextField
                label="Frete Motorista"
                name="value"
                type="number"
                inputProps={{ step: "0.01", min: "0" }}
                placeholder="0,00"
                value={formik.values.value ?? ''}
                onChange={(e) => {
                  formik.handleChange(e);
                  formik.setFieldError('value', '');
                }}
                onBlur={formik.handleBlur}
                error={formik.touched.value && Boolean(formik.errors.value)}
                helperText={formik.touched.value && formik.errors.value}
                disabled={view}
                fullWidth
                color="secondary"
                InputProps={{
                  startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                }}
              />
            </WrapperInputCustom>
          </Column>

          <Column>
            <WrapperInputCustom>
              <TextField
                label="Valor do pedágio"
                name="toll_value"
                type="number"
                inputProps={{ step: "0.01", min: "0" }}
                placeholder="0,00"
                value={formik.values.toll_value ?? ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.toll_value && Boolean(formik.errors.toll_value)}
                helperText={formik.touched.toll_value && formik.errors.toll_value}
                disabled={view}
                fullWidth
                color="secondary"
                InputProps={{
                  startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                }}
              />
            </WrapperInputCustom>
          </Column>
        </Row>

        <Row>
          <Column>
            <WrapperInputCustom>
              <TextField
                label="Adiantamento Motorista"
                name="adiantamento_motorista"
                type="number"
                inputProps={{ step: "0.01", min: "0" }}
                placeholder="0,00"
                value={formik.values.adiantamento_motorista ?? ''}
                onChange={formik.handleChange}
                onBlur={formik.handleBlur}
                error={formik.touched.adiantamento_motorista && Boolean(formik.errors.adiantamento_motorista)}
                helperText={formik.touched.adiantamento_motorista && formik.errors.adiantamento_motorista}
                disabled={view}
                fullWidth
                color="secondary"
                InputProps={{
                  startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                }}
              />
            </WrapperInputCustom>
          </Column>

          <Column>
            <WrapperInputCustom>
              <AutocompleteWrapper ref={dropdownNaturezaAdiantamentoRef}>
                <TextField
                  label="Natureza Adiantamento"
                  name="naturezaAdiantamentoId"
                  value={naturezaAdiantamentoInput}
                  onChange={(e) => {
                    setNaturezaAdiantamentoInput(e.target.value);
                    setShowDropdownNaturezaAdiantamento(true);
                  }}
                  onFocus={() => setShowDropdownNaturezaAdiantamento(true)}
                  placeholder="Digite para buscar..."
                  fullWidth
                  color="secondary"
                  disabled={view}
                />
                {showDropdownNaturezaAdiantamento && naturezasAdiantamentoFiltradas.length > 0 && (
                  <AutocompleteDropdown>
                    {naturezasAdiantamentoFiltradas.map((natureza) => (
                      <AutocompleteItem
                        key={natureza.id}
                        onClick={() => handleSelecionarNaturezaAdiantamento(natureza)}
                      >
                        {natureza.codigo || ''} - {natureza.descricao || ''}
                      </AutocompleteItem>
                    ))}
                  </AutocompleteDropdown>
                )}
              </AutocompleteWrapper>
            </WrapperInputCustom>
          </Column>

          <Column>
            <WrapperInputCustom>
              <AutocompleteWrapper ref={dropdownNaturezaFreteRef}>
                <TextField
                  label="Natureza Frete"
                  name="naturezaFreteId"
                  value={naturezaFreteInput}
                  onChange={(e) => {
                    setNaturezaFreteInput(e.target.value);
                    setShowDropdownNaturezaFrete(true);
                  }}
                  onFocus={() => setShowDropdownNaturezaFrete(true)}
                  placeholder="Digite para buscar..."
                  fullWidth
                  color="secondary"
                  disabled={view}
                />
                {showDropdownNaturezaFrete && naturezasFreteFiltradas.length > 0 && (
                  <AutocompleteDropdown>
                    {naturezasFreteFiltradas.map((natureza) => (
                      <AutocompleteItem
                        key={natureza.id}
                        onClick={() => handleSelecionarNaturezaFrete(natureza)}
                      >
                        {natureza.codigo || ''} - {natureza.descricao || ''}
                      </AutocompleteItem>
                    ))}
                  </AutocompleteDropdown>
                )}
              </AutocompleteWrapper>
            </WrapperInputCustom>
          </Column>
        </Row>
      </Container>
    </Section>
  );
}

export default Totais;

